import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useApi } from '../../../hooks/useApi';

const stateColors = {
  running: 'success',
  stopped: 'error',
  pending: 'warning',
  stopping: 'warning',
  'shutting-down': 'warning',
  terminated: 'error',
};

function EC2Section() {
  const api = useApi();
  const [sortField, setSortField] = useState(() => {
    try { return localStorage.getItem('admin_ec2_sort_field') || 'name'; } catch { return 'name'; }
  });
  const [sortDir, setSortDir] = useState(() => {
    try { return localStorage.getItem('admin_ec2_sort_dir') || 'asc'; } catch { return 'asc'; }
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-ec2-status'],
    queryFn: () => api.get('/api/admin/ec2-status'),
    refetchInterval: 30000,
  });

  const instances = data?.instances || [];
  const sorted = [...instances].sort((a, b) => {
    const aVal = (a[sortField] || '').toString().toLowerCase();
    const bVal = (b[sortField] || '').toString().toLowerCase();
    return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
  });

  const handleSort = (field) => {
    if (sortField === field) {
      const next = sortDir === 'asc' ? 'desc' : 'asc';
      setSortDir(next);
      try { localStorage.setItem('admin_ec2_sort_dir', next); } catch { /* ignore */ }
    } else {
      setSortField(field);
      setSortDir('asc');
      try {
        localStorage.setItem('admin_ec2_sort_field', field);
        localStorage.setItem('admin_ec2_sort_dir', 'asc');
      } catch { /* ignore */ }
    }
  };

  if (isLoading) return <CircularProgress size={24} />;
  if (error) return <Alert severity="error">{error.message}</Alert>;

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            {['id', 'name', 'type', 'state', 'publicIp', 'region'].map((col) => (
              <TableCell
                key={col}
                onClick={() => handleSort(col)}
                sx={{ cursor: 'pointer', fontWeight: sortField === col ? 700 : 400 }}
              >
                {col.charAt(0).toUpperCase() + col.slice(1)}
                {sortField === col && (sortDir === 'asc' ? ' ↑' : ' ↓')}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map((inst) => (
            <TableRow key={inst.id}>
              <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{inst.id}</TableCell>
              <TableCell>{inst.name || '—'}</TableCell>
              <TableCell>{inst.type}</TableCell>
              <TableCell>
                <Chip label={inst.state} size="small" color={stateColors[inst.state] || 'default'} />
              </TableCell>
              <TableCell>{inst.publicIp || '—'}</TableCell>
              <TableCell>{inst.region}</TableCell>
            </TableRow>
          ))}
          {sorted.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} align="center">No instances found</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function AWSCostsSection() {
  const api = useApi();

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-aws-costs'],
    queryFn: () => api.get('/api/admin/aws-costs'),
    refetchInterval: 3600000,
  });

  if (isLoading) return <CircularProgress size={24} />;
  if (error) return <Alert severity="error">{error.message}</Alert>;

  const costs = (data?.costs || []).filter((c) => parseFloat(c.cost) > 0).sort((a, b) => b.cost - a.cost);

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Monthly Total: ${parseFloat(data?.totalCost || 0).toFixed(2)}
      </Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Service</TableCell>
              <TableCell align="right">Cost (USD)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {costs.map((c) => (
              <TableRow key={c.service}>
                <TableCell>{c.service}</TableCell>
                <TableCell align="right">${parseFloat(c.cost).toFixed(2)}</TableCell>
              </TableRow>
            ))}
            {costs.length === 0 && (
              <TableRow>
                <TableCell colSpan={2} align="center">No cost data</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function AdminInfrastructure() {
  const navigate = useNavigate();

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <IconButton onClick={() => navigate('/admin')} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4">Infrastructure</Typography>
      </Box>

      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">EC2 Instances</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <EC2Section />
        </AccordionDetails>
      </Accordion>

      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">AWS Costs (Current Month)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <AWSCostsSection />
        </AccordionDetails>
      </Accordion>

    </Box>
  );
}

export default AdminInfrastructure;
